import React, { useState } from "react";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";
import {
  XMarkIcon,
  PlusIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UserIcon,
  IdentificationIcon,
} from "@heroicons/react/24/solid";
import Tabs from "../../components/controls/Tabs";
import ServiceCard from "../../components/controls/ServiceCard";
import ProductCard from "../../components/controls/ProductCard";
import FormInput from "../../components/controls/FormInput";
import PlacesAutocomplete from "../../components/Places/PlacesAutocomplete";

const OrderModal = ({ isOpen, onClose, orderData, isAdmin }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("general");

  const tabs = [
    { id: "general", label: t("modal.generalInfo") },
    { id: "orderItems", label: t("modal.productsServices") },
  ];

  const handleChange = (name, value) => {};

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission for admins if needed
    // (e.g., send updates to the server)
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={t("modal.title")}
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
        zIndex: "50",
        content: {
          width: "90%",
          maxWidth: "1200px",
          margin: "auto",
          borderRadius: "12px",
          padding: "0",
          display: "flex",
          flexDirection: "column",
          border: 0,
        },
      }}
    >
      <div className="bg-accent text-white p-4 flex justify-between items-center ">
        <h1 className="text-xl">{t("modal.orderDetails")}</h1>
        <XMarkIcon className="w-6 h-6 cursor-pointer" onClick={onClose} />
      </div>
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        fullWidth={true}
      />
      <div className="flex-grow overflow-y-auto p-4 text-primary">
        <form className="p-4 text-primary" onSubmit={handleSubmit}>
          {activeTab === "general" && (
            <div className="space-y-4">
              <FormInput
                label={t("stepOne.eventName.label")}
                name="eventName"
                value={orderData.name}
                onChange={handleChange}
                placeholder={t("stepOne.eventName.placeholder")}
                disabled={!isAdmin}
                showRequiredOrOptional={false}
                icon={<IdentificationIcon className="w-5 h-5 text-gray-500" />}
              />

              <FormInput
                label={t("stepOne.eventDate.label")}
                name="eventDate"
                value={new Date(orderData.eventDate).toLocaleDateString()}
                onChange={handleChange}
                placeholder={t("stepOne.eventDate.placeholder")}
                disabled={!isAdmin}
                showRequiredOrOptional={false}
                icon={
                  <CalendarIcon className="w-5 h-5 text-gray-500"></CalendarIcon>
                }
              />

              <PlacesAutocomplete
                title={t("providerEdit.companyAddress")}
                onSelect={(e) => {
                  handleChange("location", e);
                  handleChange("companyAddress", e.formattedAddress);
                }}
                initialAddress={orderData.formattedAddress}
                showOptional={false}
                canEdit={false}
              />

              <FormInput
                label={t("cart.totalPrice")}
                name="totalAmount"
                value={`${orderData.totalAmount.toFixed(2)} $`}
                onChange={handleChange}
                placeholder={t("cart.totalPricePlaceholder")}
                disabled={true}
                showRequiredOrOptional={false}
                icon={<CurrencyDollarIcon className="w-5 h-5 text-gray-500" />}
              />

              <div>
                <label className="font-medium">{t("cart.order_status")}</label>
                <div>
                  {(() => {
                    let statusClass = "";
                    let statusLabel = "";

                    switch (orderData.status) {
                      case "event-in-creation":
                        statusClass = "bg-yellow-500 text-black";
                        statusLabel = t("status.eventInCreation");
                        break;
                      case "paid":
                        statusClass = "bg-green-500 text-white";
                        statusLabel = t("status.paid");
                        break;
                      case "done":
                        statusClass = "bg-red-500 text-white";
                        statusLabel = t("status.done");
                        break;
                      default:
                        statusClass = "bg-gray-500 text-white";
                        statusLabel = t("status.eventInCreation");
                    }

                    return (
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${statusClass}`}
                      >
                        {statusLabel}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {activeTab === "orderItems" && (
            <div>
              {/* Order Items (Products and Services) */}
              <div className="space-y-6">
                {orderData.items?.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {orderData.items.map((item) =>
                      !item.isProduct ? (
                        <ServiceCard
                          key={item.id}
                          service={{
                            id: item.id,
                            name: item.name,
                            description: item.description,
                            price: item.price,
                            imageUrl: item.imageUrl,
                            category: item.category,
                          }}
                        />
                      ) : item.isProduct ? (
                        <ProductCard
                          key={item.id}
                          product={{
                            id: item.id,
                            name: item.name,
                            description: item.description,
                            price: item.price,
                            imageUrl: item.imageUrl,
                            category: item.category,
                          }}
                        />
                      ) : (
                        item.id
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </form>
      </div>
      {/* Actions - Save/Cancel */}
      <div className="sticky bottom-0 bg-accent p-4 flex justify-end gap-4 rounded-b-lg border-t">
        <button
          type="button"
          className="flex items-center gap-2 px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium rounded-full"
          onClick={onClose}
        >
          <XMarkIcon className="w-5 h-5" />
          {t("modal.cancel")}
        </button>
        {isAdmin && (
          <button
            type="button"
            onClick={handleSubmit}
            className="flex items-center gap-2 px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-full"
          >
            <PlusIcon className="w-5 h-5" />
            {t("modal.save")}
          </button>
        )}
      </div>
    </Modal>
  );
};

export default OrderModal;
